import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import type PrescriptionJournalEvent from "parog-web/models/prescription-journal-event";
import tWithDefault from "parog-web/utils/t-with-default";
import type AlertTriggeredJournalEvent from "./alert-triggered-journal-event";

//---- Automatically generated, don't update this part ----

type PrescriptionReasonStringEnum = "nephro-mrc" | "nephro-transplant";

/**
 * This event represents a telemonitoring prescription
 */
export default class TlsPrescribedJournalEvent
  extends JournalEvent
  implements PrescriptionJournalEvent
{
  static override type = "tls-prescribed-journal-event";

  /**
   * provides the right info about the patient and its health center
   */
  @attr() declare snapshot: any;

  /**
   * The start of the prescription
   */
  @attr("date") declare startDate: Date;

  @attr("string") declare prescriptionReason: PrescriptionReasonStringEnum;

  /**
   * The patient's weight at the time of the prescription
   */
  @attr("number") declare currentWeight: number;

  @belongsTo("alert-triggered-journal-event", {
    async: false,
    inverse: "tlsPrescribedJournalEvent"
  })
  declare alertTriggeredJournalEvent: AlertTriggeredJournalEvent;

  static override validations(intl: IntlService) {
    return {
      startDate: validatePresence({
        presence: true,
        description: intl.t(
          "validations.tls-prescribed-journal-event.start-date"
        )
      }),
      prescriptionReason: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.tls-prescribed-journal-event.prescription-reason"
          )
        }),
        validateInclusion({
          list: ["nephro-mrc", "nephro-transplant"],
          description: intl.t(
            "validations.tls-prescribed-journal-event.prescription-reason"
          )
        })
      ],
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.tls-prescribed-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      currentWeight: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.tls-prescribed-journal-event.current-weight"
        )
      })
    };
  }

  //---- End of generated code ----
  @belongsTo("patient", { async: false, inverse: "tlsPrescribedJournalEvents" })
  declare patient: Patient;

  @attr("string")
  declare uuid: string;

  get endDate(): Date {
    const end = new Date(this.startDate);
    end.setDate(end.getDate() + 26 * 7 - 1); // 26 weeks minus 1 day
    return end;
  }

  get pdfUrl() {
    return `/api/v1/tls_prescriptions/${this.id}`;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "tls-prescribed-journal-event": TlsPrescribedJournalEvent;
  }
}
